import { type ReactNode } from 'react';

export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: string; actions?: ReactNode }) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="text-xl font-bold text-slate-900">{title}</h1>
        {subtitle ? <p className="mt-1 text-sm text-slate-500">{subtitle}</p> : null}
      </div>
      {actions ? <div className="flex items-center gap-2">{actions}</div> : null}
    </div>
  );
}

export function StatCard({ label, value, hint, tone = 'default' }: { label: string; value: ReactNode; hint?: string; tone?: 'default' | 'good' | 'warn' | 'bad' }) {
  const toneClass =
    tone === 'good' ? 'text-seagreen-600' : tone === 'warn' ? 'text-amber-600' : tone === 'bad' ? 'text-rose-600' : 'text-slate-900';
  return (
    <div className="card px-4 py-3.5">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`mt-1 text-lg font-bold tabular-nums ${toneClass}`}>{value}</div>
      {hint ? <div className="mt-0.5 text-xs text-slate-400">{hint}</div> : null}
    </div>
  );
}

// One entry per status/enum string either backend module actually returns
// (m24-ehealth, m25-public-health) — see each module's prisma/schema.prisma
// for the authoritative enum lists. A handful of strings are intentionally
// shared across unrelated enums where the tone reads the same either way
// (e.g. ROUTINE covers both ReferralPriority and HygieneInspection.source;
// TRIAGED covers both Referral.state and SanitaryComplaint.status).
// OutbreakStatus.ACTIVE is the one real semantic clash — "operating
// normally" (HealthCertApplication.operatingStatus) vs. "outbreak still
// live" read oppositely — so the Surveillance screen renders outbreak
// status with its own small pill instead of reusing StatusBadge.
const STATUS_TONES: Record<string, string> = {
  ACTIVE: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  OPEN: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  APPROVED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  GRANTED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  PAID: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  DONE: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  DRAFT: 'bg-slate-100 text-slate-600 ring-slate-200',
  PENDING: 'bg-sky-50 text-sky-700 ring-sky-200',
  REQUESTED: 'bg-sky-50 text-sky-700 ring-sky-200',
  SUBMITTED: 'bg-sky-50 text-sky-700 ring-sky-200',
  IN_REVIEW: 'bg-sky-50 text-sky-700 ring-sky-200',
  REJECTED: 'bg-rose-50 text-rose-700 ring-rose-200',
  DENIED: 'bg-rose-50 text-rose-700 ring-rose-200',
  FAILED: 'bg-rose-50 text-rose-700 ring-rose-200',
  REVOKED: 'bg-rose-50 text-rose-700 ring-rose-200',
  CANCELLED: 'bg-slate-100 text-slate-500 ring-slate-200',
  CLOSED: 'bg-slate-100 text-slate-500 ring-slate-200',
  EXPIRED: 'bg-slate-100 text-slate-500 ring-slate-200',
  SUSPENDED: 'bg-amber-50 text-amber-700 ring-amber-200',
  WARN: 'bg-amber-50 text-amber-700 ring-amber-200',

  // m24-ehealth — Appointment.status
  BOOKED: 'bg-sky-50 text-sky-700 ring-sky-200',
  COMPLETED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  NO_SHOW: 'bg-rose-50 text-rose-700 ring-rose-200',

  // m24-ehealth — Referral.priority
  ROUTINE: 'bg-slate-100 text-slate-600 ring-slate-200',
  URGENT: 'bg-amber-50 text-amber-700 ring-amber-200',
  EMERGENCY: 'bg-rose-50 text-rose-700 ring-rose-200',

  // m24-ehealth — Referral.track
  STANDARD: 'bg-slate-100 text-slate-600 ring-slate-200',
  OVERSEAS_TREATMENT: 'bg-violet-50 text-violet-700 ring-violet-200',

  // m24-ehealth — Referral.state (STANDARD track + OVERSEAS_TREATMENT track)
  REFERRED: 'bg-sky-50 text-sky-700 ring-sky-200',
  TRIAGED: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  SCHEDULED: 'bg-sky-50 text-sky-700 ring-sky-200',
  SEEN: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  BOARD_REVIEW: 'bg-violet-50 text-violet-700 ring-violet-200',
  FINANCE_APPROVAL: 'bg-violet-50 text-violet-700 ring-violet-200',
  LOGISTICS: 'bg-violet-50 text-violet-700 ring-violet-200',
  TREATMENT: 'bg-violet-50 text-violet-700 ring-violet-200',
  FOLLOWUP: 'bg-indigo-50 text-indigo-700 ring-indigo-200',

  // m24-ehealth — PrescriptionState / VaccinationCertStatus / HealthDocStatus
  ISSUED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  DISPENSED: 'bg-slate-100 text-slate-500 ring-slate-200',
  VALID: 'bg-emerald-50 text-emerald-700 ring-emerald-200',

  // m24-ehealth — LabOrderStatus
  ORDERED: 'bg-sky-50 text-sky-700 ring-sky-200',
  SENT_TO_LAB: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  RESULT_READY: 'bg-emerald-50 text-emerald-700 ring-emerald-200',

  // m24-ehealth — LabResultReleaseStatus
  PENDING_RELEASE: 'bg-amber-50 text-amber-700 ring-amber-200',
  RELEASED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  WITHHELD: 'bg-rose-50 text-rose-700 ring-rose-200',

  // m24-ehealth — BirthNotification.pushStatus
  PENDING_DISPATCH: 'bg-amber-50 text-amber-700 ring-amber-200',
  DISPATCHED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',

  // m24-ehealth — Facility.kind (informational, not a lifecycle status)
  CLINIC: 'bg-slate-100 text-slate-600 ring-slate-200',
  HOSPITAL: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  LAB_PARTNER: 'bg-violet-50 text-violet-700 ring-violet-200',

  // m25-public-health — HealthCertApplication.state
  INSPECTION_SCHEDULED: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  FEE_PENDING: 'bg-amber-50 text-amber-700 ring-amber-200',
  WITHDRAWN: 'bg-slate-100 text-slate-500 ring-slate-200',

  // m25-public-health — HygieneGrade (all three are PASSING grades — no
  // grade at all, rendered separately as "No grade", is the failing case)
  A: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  B: 'bg-sky-50 text-sky-700 ring-sky-200',
  C: 'bg-amber-50 text-amber-700 ring-amber-200',

  // m25-public-health — HygieneInspection.source
  RENEWAL: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  INITIAL: 'bg-slate-100 text-slate-600 ring-slate-200',
  COMPLAINT: 'bg-rose-50 text-rose-700 ring-rose-200',

  // m25-public-health — FoodHandlerCertState
  APPLIED: 'bg-sky-50 text-sky-700 ring-sky-200',
  COURSE_BOOKED: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  TRAINING_COMPLETED: 'bg-violet-50 text-violet-700 ring-violet-200',
  MEDICAL_SCREENED: 'bg-violet-50 text-violet-700 ring-violet-200',

  // m25-public-health — ImportPermitStatus
  HOLD: 'bg-amber-50 text-amber-700 ring-amber-200',

  // m25-public-health — ComplaintStatus
  INSPECTION_TRIGGERED: 'bg-violet-50 text-violet-700 ring-violet-200',
  RESOLVED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  DISMISSED: 'bg-slate-100 text-slate-500 ring-slate-200',

  // m25-public-health — DiseaseNotificationState
  NOTIFIED: 'bg-sky-50 text-sky-700 ring-sky-200',
  UNDER_INVESTIGATION: 'bg-amber-50 text-amber-700 ring-amber-200',
  CONFIRMED: 'bg-rose-50 text-rose-700 ring-rose-200',
  RULED_OUT: 'bg-emerald-50 text-emerald-700 ring-emerald-200',

  // m25-public-health — OutbreakStatus (CONTAINED only; see ACTIVE/CLOSED note above)
  CONTAINED: 'bg-amber-50 text-amber-700 ring-amber-200',

  // m25-public-health — ContactTraceEntry.monitoringStatus
  MONITORING: 'bg-sky-50 text-sky-700 ring-sky-200',
  CLEARED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  SYMPTOMATIC: 'bg-rose-50 text-rose-700 ring-rose-200',

  // m25-public-health — PremisesType (informational)
  FOOD_BUSINESS: 'bg-slate-100 text-slate-600 ring-slate-200',
  ACCOMMODATION: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  SALON: 'bg-violet-50 text-violet-700 ring-violet-200',
  POOL: 'bg-sky-50 text-sky-700 ring-sky-200',
};

export function StatusBadge({ status }: { status: string }) {
  const tone = STATUS_TONES[status] ?? 'bg-slate-100 text-slate-600 ring-slate-200';
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ${tone}`}>
      {status.replaceAll('_', ' ')}
    </span>
  );
}

/** Outbreak.status has its own tones — ACTIVE means "still live" here, the opposite valence of OperatingStatus.ACTIVE that StatusBadge's shared map optimises for. */
export function OutbreakStatusBadge({ status }: { status: 'ACTIVE' | 'CONTAINED' | 'CLOSED' }) {
  const tone =
    status === 'ACTIVE'
      ? 'bg-rose-50 text-rose-700 ring-rose-200'
      : status === 'CONTAINED'
        ? 'bg-amber-50 text-amber-700 ring-amber-200'
        : 'bg-slate-100 text-slate-500 ring-slate-200';
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ${tone}`}>{status}</span>;
}

export function Spinner({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="flex items-center gap-2 px-4 py-8 text-sm text-slate-500">
      <span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-brand-600" />
      {label}
    </div>
  );
}

export function ErrorNote({ message }: { message: string }) {
  return (
    <div className="rounded-lg border border-rose-200 bg-rose-50 px-3.5 py-2.5 text-sm text-rose-700">{message}</div>
  );
}

export function EmptyState({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="px-4 py-10 text-center">
      <div className="text-sm font-semibold text-slate-600">{title}</div>
      {hint ? <div className="mt-1 text-xs text-slate-400">{hint}</div> : null}
    </div>
  );
}

export function Modal({ title, onClose, children, wide }: { title: string; onClose: () => void; children: ReactNode; wide?: boolean }) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink-950/50 p-4 pt-[8vh]" onClick={onClose}>
      <div
        className={`card w-full ${wide ? 'max-w-3xl' : 'max-w-lg'} p-5`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-bold text-slate-900">{title}</h2>
          <button className="rounded-md px-2 py-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function Field({ label, children, className = '' }: { label: string; children: ReactNode; className?: string }) {
  return (
    <label className={`block ${className}`}>
      <span className="field-label">{label}</span>
      {children}
    </label>
  );
}

export function Tabs<T extends string>({ tabs, value, onChange }: { tabs: Array<{ key: T; label: string }>; value: T; onChange: (key: T) => void }) {
  return (
    <div className="mb-4 flex gap-1 rounded-xl bg-slate-200/60 p-1">
      {tabs.map((t) => (
        <button
          key={t.key}
          onClick={() => onChange(t.key)}
          className={`flex-1 rounded-lg px-3 py-1.5 text-sm font-semibold transition ${
            value === t.key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}
